import React, { Fragment, useEffect, useState } from "react";
import Loading from "../components/Loading";
import {
  clearImpersonate,
  isAssumingUserIdentity,
} from "shared-ui/src/utils/clientmanagement";
import { clearImpersonateAndReturnToCs } from "functions/handleImpersonateAccount";
import AuthError from "./AuthError";
import { useClaims } from "../auth/ClaimsProvider";
import ReactRouterClassWrapper from "../components/ReactRouterClassWrapper";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../store";
import { getPicklist } from "../modules/picklist/actions/picklistActions";
import { useNavHamburger } from "./useNavHamburger";
import {
  getIntegrationsCollection,
  getIntegrationsDefinitions,
} from "../modules/rtk/integrationsCollector/integrationsCollectorSlice";
import { getMyDevices } from "../modules/rtk/device/deviceSlice";
import { getMyTeams } from "../modules/rtk/team/teamSlice";
import { getFSAs } from "../modules/rtk/contract/contractSlice";
import { loadProjects } from "../modules/rtk/project/projectSlice";
import { getAccounts } from "../modules/rtk/accountAdmin/accountAdminSlice";
import { getMonitoringAlerts } from "../modules/rtk/alert/alertSlice";
import { ShiOnePermission } from "../auth/authorizationTypes";
import {
  getMyAdditionalContactRequests,
  getMyServiceRequests,
} from "../modules/rtk/serviceRequest/serviceRequestSlice";
import { getKnowledgebase } from "../modules/knowledgebase/actions/knowledgebaseActions";
import { AppendIfPrefixExists, SitePaths } from "../modules/sitePaths";
import {
  parseInvalidNewURLWithSitePrefix,
  parseNewURLWithSitePrefix,
} from "../functions/getCorrectURLBasedOnSitePrefix";
import { getSite } from "../modules/site/actions/siteActions";
import MainContainer from "shared-ui/src/components/MainContainer";
import UnifiedHeader from "@containers/UnifiedHeader.js";
import { useImpersonationStore } from "../functions/hooks/useImpersonationStore.js";
import UnifiedSideNav from "@features/unified-side-nav-feature/components/UnifiedSideNav.js";
import { UnificationAnnouncementBanner } from "@basecomponents/UnificationAnnouncementBanner";
import { UnifiedNavProvider } from "@features/unified-side-nav-feature/context/UnifiedNavContext";
import { StorefrontSwitcherProvider } from "@features/unified-side-nav-feature/storefront-switcher/StorefrontSwitcherContext";
import { OrderDrawerProvider } from "@features/orders-features/components/order-drawer";
import shiColors from "shared-ui/src/theme/shiColors";
import { Chip } from "@mui/material";

interface ContentProps {
  isOpenSideBar: boolean;
  isDesktop: boolean;
  children?: React.ReactNode;
}

const Content = ({ isOpenSideBar, isDesktop, children }: ContentProps) => {
  return (
    <MainContainer isDrawerOpen={isOpenSideBar && isDesktop}>
      {children}
    </MainContainer>
  );
};

interface DashboardProps {
  children?: React.ReactNode;
}

export default function Dashboard({ children }: DashboardProps) {
  const dispatch = useDispatch<AppDispatch>();

  const [initialDataLoad, setInitialDataLoad] = useState(false);
  const { isDesktop, isOpenSideBar } = useNavHamburger();

  const { fetchingSite, fetchSiteComplete } = useSelector(
    (state: RootState) => state.site,
  );

  const { picklist, fetchingPicklist } = useSelector(
    (state: RootState) => state.picklist,
  );

  const {
    claims,
    claimsLoading,
    claimsFetching,
    claimsError,
    isCustomerAdmin,
    isCustomerUser,
    isShi,
    isVirtualAdmin,
    getClaimValue,
    hasPermission,
    hasRoleIn,
  } = useClaims();

  const sitePrefix = getClaimValue("sitePrefix") ?? undefined;

  const claimsLoaded = !claimsLoading && claims.length > 0;
  const isShiOrVirtualAdmin = isShi() || isVirtualAdmin();
  const hasUserWithClaims =
    claimsLoaded && (isCustomerUser() || isShiOrVirtualAdmin);
  const noUserProfile =
    !claimsLoading &&
    claims.length > 0 &&
    !isCustomerUser() &&
    !isShiOrVirtualAdmin;
  const startInitialDataLoad = hasUserWithClaims && !initialDataLoad;

  const contactAccountId = getClaimValue("accountId");
  const userId = getClaimValue("userId");
  const userEmail = getClaimValue("userEmail");
  const isShiUser = isShiOrVirtualAdmin;
  const isAdmin = isCustomerAdmin();
  const isShiResource = hasRoleIn("ShiResource");

  const impersonateState = useImpersonationStore();

  useEffect(() => {
    if (startInitialDataLoad) {
      setInitialDataLoad(true);
      dispatch(getPicklist());
      dispatch(getIntegrationsCollection());
      if (isAdmin || isShi()) {
        dispatch(getIntegrationsDefinitions());
      }
      dispatch(getMyDevices());
      dispatch(getMyTeams());
      if (
        isAdmin ||
        isShiResource ||
        hasPermission(ShiOnePermission.serviceContractRead)
      ) {
        dispatch(getFSAs());
      }
      if (isAdmin) {
        dispatch(getAccounts());
      }
      if (
        hasPermission(ShiOnePermission.azureMonitoringAlertRead) ||
        hasPermission(ShiOnePermission.awsMonitoringAlertRead)
      ) {
        dispatch(getMonitoringAlerts());
      }
      dispatch(getMyServiceRequests());
      dispatch(getMyAdditionalContactRequests());
      dispatch(getKnowledgebase());
    } else if (noUserProfile) {
      if (!isAssumingUserIdentity()) {
        clearImpersonate(window.location.origin, false);
      }
      window.location.href = AppendIfPrefixExists("/noprofile");
    }
  }, [startInitialDataLoad, noUserProfile]);

  useEffect(() => {
    const { user, account } = impersonateState;

    if (!user && !account) return;
    if (!claimsLoaded) return;

    let mismatch = false;

    if (
      account &&
      contactAccountId &&
      Number(account) !== Number(contactAccountId) &&
      isShiUser
    ) {
      mismatch = true;
    }

    if (user && isShiUser) {
      if (user.userId && String(user.userId) !== String(userId)) {
        mismatch = true;
      }
      // The demo launcher still impersonates by Autotask contact id, so it carries
      // neither a user id nor an email to compare against.
      if (
        user.email &&
        user.email.toLowerCase() !== (userEmail ?? "").toLowerCase()
      ) {
        mismatch = true;
      }
    }

    const reloadKey = `impersonation-mismatch-${user?.userId ?? ""}-${user?.email ?? ""}-${account ?? ""}`;
    if (!mismatch) {
      sessionStorage.removeItem(reloadKey);
      return;
    }

    if (sessionStorage.getItem(reloadKey)) {
      sessionStorage.removeItem(reloadKey);
      clearImpersonateAndReturnToCs();
    } else {
      sessionStorage.setItem(reloadKey, "true");
      window.location.reload();
    }
  }, [
    impersonateState,
    claimsLoaded,
    contactAccountId,
    userId,
    userEmail,
    isShiUser,
  ]);

  useEffect(() => {
    const pathNames =
      window.location.pathname[0] === "/"
        ? window.location.pathname.slice(1).split("/")
        : window.location.pathname.split("/");
    const isBaseSite = SitePaths.includes(pathNames[0]);

    const url = new URL(window.location.href);
    const isInternalRoute = pathNames[0] === "internal";
    if (isBaseSite && sitePrefix && !isInternalRoute) {
      window.location.href = parseNewURLWithSitePrefix(url, sitePrefix).href;
    }

    if (
      claimsLoaded &&
      pathNames[1] !== "noprofile" &&
      ((sitePrefix && !isBaseSite && sitePrefix !== pathNames[0]) ||
        (!sitePrefix && !isBaseSite))
    ) {
      const newUrl = parseInvalidNewURLWithSitePrefix(url, sitePrefix ?? "");
      if (window.location.href !== newUrl.toString()) {
        window.location.href = newUrl.href;
      }
    }

    if (sitePrefix && !fetchingSite && !fetchSiteComplete) {
      dispatch(
        getSite({
          prefix: sitePrefix,
        }),
      );
    }
  }, [claimsLoaded, sitePrefix, fetchingSite, fetchSiteComplete, dispatch]);

  useEffect(() => {
    if (!claimsLoaded) return;
    if (isAdmin) {
      dispatch(loadProjects());
    }
  }, [claimsLoaded, isAdmin, dispatch]);

  const pathNames =
    window.location.pathname[0] === "/"
      ? window.location.pathname.slice(1).split("/")
      : window.location.pathname.split("/");

  if (sitePrefix && sitePrefix !== pathNames[0]) {
    return <Loading />;
  }

  if (
    claimsError ||
    (!claimsLoading && !claimsFetching && claims.length === 0)
  ) {
    return (
      <ReactRouterClassWrapper>{() => <AuthError />}</ReactRouterClassWrapper>
    );
  }

  if (claimsLoading || !claimsLoaded || !picklist || fetchingPicklist) {
    return <Loading />;
  }

  return (
    <UnifiedNavProvider>
      <StorefrontSwitcherProvider>
        <OrderDrawerProvider>
          <div>
            {import.meta.env.DIST_ENV === "demo" && (
              <Fragment>
                <div
                  style={{
                    position: "fixed",
                    zIndex: 1150,
                    left: "50%",
                    transform: "translateX(-50%)",
                    top: "7px",
                  }}
                >
                  <Chip
                    sx={{
                      borderColor: shiColors.blue.DEFAULT,
                    }}
                    variant="outlined"
                    label={"Demo In Progress"}
                  />
                </div>
              </Fragment>
            )}
            <UnifiedHeader />
            <Content isDesktop={isDesktop} isOpenSideBar={isOpenSideBar}>
              <>
                <div className="flex flex-row min-h-full">
                  <UnifiedSideNav />
                  <div className="flex-1 min-w-0 pl-6 pr-4 pb-4">
                    <UnificationAnnouncementBanner />
                    <div className="pb-4">{children}</div>
                  </div>
                </div>
              </>
            </Content>
          </div>
        </OrderDrawerProvider>
      </StorefrontSwitcherProvider>
    </UnifiedNavProvider>
  );
}
